import { gql } from '@apollo/client';
import * as React from 'react';

import type { EligibleBillingAccountFragment } from './__generated__/useAccountPicker.gql';
import type { IPaymentAccountOption } from './PaymentAccountPickerBase';

/**
 * The `eligibleBillingAccounts` selection every billing flow makes.
 * Interpolated into each flow's query so the generated fragment type and this
 * hook's input cannot drift from the schema — or from each other.
 */
export const ELIGIBLE_BILLING_ACCOUNT_FRAGMENT = gql`
  fragment EligibleBillingAccount on FinancialAccount {
    id
    name
    mask
    subtype
    bank {
      id
      name
    }
  }
`;

export interface IUseAccountPickerOptions {
  /** Straight off the bootstrap query — undefined while it is in flight. */
  eligibleBillingAccounts: readonly EligibleBillingAccountFragment[] | undefined;
  loading: boolean;
  /** The host owns the selection state; the hook only derives from it. */
  selectedAccountId: string | null;
  /** Store the new selection. */
  onSelect: (accountId: string) => void;
  /**
   * A different account was picked, so any disclosure or refusal shown for
   * the old one is stale. Fired before `onSelect`, and never on a
   * same-account reselect.
   */
  onAccountChanged: () => void;
  /**
   * A refetch (a new bank landing, a quote refresh) dropped the selected
   * account from the eligible list — clear the selection, or a stale id
   * wedges the authorize render.
   */
  onSelectionInvalidated: () => void;
}

export interface IAccountPicker {
  accounts: IPaymentAccountOption[];
  selectedAccount: IPaymentAccountOption | null;
  selectAccount: (accountId: string) => void;
}

/**
 * The account-picking half both billing flows share: maps the query's
 * eligible accounts into picker options, resolves the selection against
 * them, and tells the host when its selection-adjacent state went stale.
 * State stays with the host — checkout keeps its reducer, the account
 * switch its local state — so the hook reports transitions rather than
 * owning them.
 *
 * @param options.eligibleBillingAccounts The query's `...EligibleBillingAccount` selection, or undefined mid-flight.
 * @param options.loading Whether the bootstrap query is still in flight — invalidation is suppressed until it settles.
 * @param options.selectedAccountId The host-owned selection, or null before a choice is made.
 * @param options.onSelect Stores a new selection in the host's state.
 * @param options.onAccountChanged Fired before `onSelect` when a *different* account is picked.
 * @param options.onSelectionInvalidated Fired when a refetch dropped the selected account from the list.
 * @returns The mapped picker options, the resolved selection, and the `selectAccount` handler to wire into the picker.
 */
export function useAccountPicker({
  eligibleBillingAccounts,
  loading,
  selectedAccountId,
  onSelect,
  onAccountChanged,
  onSelectionInvalidated,
}: IUseAccountPickerOptions): IAccountPicker {
  const accounts = React.useMemo(
    () =>
      // The server lists only accounts carrying both; this narrows the nullable wire type.
      (eligibleBillingAccounts ?? []).flatMap((account) =>
        account.mask && account.subtype
          ? [
              {
                id: account.id,
                name: account.name,
                mask: account.mask,
                bankName: account.bank.name,
                subtype: account.subtype,
              },
            ]
          : []
      ),
    [eligibleBillingAccounts]
  );

  const selectedAccount = accounts.find((account) => account.id === selectedAccountId) ?? null;

  React.useEffect(() => {
    if (selectedAccountId !== null && selectedAccount === null && !loading) {
      onSelectionInvalidated();
    }
  }, [selectedAccountId, selectedAccount, loading, onSelectionInvalidated]);

  const selectAccount = (accountId: string) => {
    if (accountId !== selectedAccountId) {
      onAccountChanged();
    }
    onSelect(accountId);
  };

  return { accounts, selectedAccount, selectAccount };
}
